import { compareDesc } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";

import { usePermissions } from "./PermissionsProvider";
import { APIReturnType } from "./types";

import { useAppRouteParams } from "@/AppRoutes";
import { getAdminAppConnectMasters } from "@/api/admin/appConnectMaster";
import { getAdminEndpointAddonMasters } from "@/api/admin/endpointAddonMasters";
import { getAdminEndpointAddons } from "@/api/admin/endpointAddons";
import { getAdminFrontendAddons } from "@/api/admin/frontendAddons";
import { getAlgorithmVersions } from "@/api/algorithmVersions";
import { getAppConnectMasters } from "@/api/appConnectMasters";
import { getAppPermissionMasters } from "@/api/appPermissionMasters";
import { getConnectMasters } from "@/api/connectMasters";
import { getConnects } from "@/api/connects";
import { getFrontendAddons } from "@/api/frontendAddons";
import { getAPIErrorDetailAndMessage } from "@/api/helpers";
import { getInvitedMembers, getMembers } from "@/api/members";
import { getOrganization } from "@/api/organizations";
import { getRoles } from "@/api/role";
import { getViewMasters } from "@/api/viewMasters";
import { AppEndpointAddonMasterRaw } from "@/domain/AppEndpointAddonMaster";
import { AppPermissionMasterRaw } from "@/domain/AppPermissionMaster";
import { EndpointAddonRaw } from "@/domain/EndpointAddon";
import { PermissionsKey, Role } from "@/domain/roles";
import { parsePermissions } from "@/helpers/permissions";
import { parseDateTimeOrISODateTime } from "@/utils/dateFnsHelper";
import { useAppAccount } from "../AppProvider/AppAccountProvider";

// 組織内プロバイダで使用するread系のエンドポイントへのアクセスを一括で管理するカスタムフック
// 権限がない場合はstateとしてnullを返す
export const useAPI = <T extends APIReturnType>(
  permissionKey: PermissionsKey
) => {
  const { me, hasPermission, isLoadedMe } = usePermissions();
  const { organizationId } = useAppRouteParams();

  const [state, setState] = useState<T | null>(null);
  const [isLoadedState, setIsLoadedState] = useState<boolean>(false);
  const [_, setError] = useState<unknown>(null);
  const { isAADeveloperOrAbove } = useAppAccount();

  const runReloadState = useCallback(async () => {
    try {
      if (!organizationId) {
        setState(null);
        return;
      }
      setIsLoadedState(false);
      // frontend_addons_ownedおよびconnect_masters_ownedは権限なしでも管理者であれば使用できるので個別関数のなかでハンドリング
      if (
        permissionKey !== "frontend_addons_owned" &&
        permissionKey !== "connect_masters_owned" &&
        !hasPermission(permissionKey, "read")
      ) {
        setState(null);
        setIsLoadedState(true);
        return;
      }
      switch (permissionKey) {
        // OrganizationsProvider
        case "organizations": {
          const response = await getOrganization({
            organizationId,
          });
          setState(response as T);
          break;
        }
        // MembersProvider
        // メンバ、招待中メンバ両方を取得
        case "members": {
          const memberResponse = await getMembers({
            organizationId,
          });
          const invitedMemberResponse = await getInvitedMembers({
            organizationId,
          });
          setState({
            members: memberResponse,
            invitedMembers: invitedMemberResponse,
          } as T);
          break;
        }
        // RolesProvider
        case "roles": {
          const response = await getRoles({ organizationId });
          const sortedResponse = [...response].sort((a, b) => {
            const _a = parseDateTimeOrISODateTime(a.created_at);
            const _b = parseDateTimeOrISODateTime(b.created_at);
            return _a && _b ? compareDesc(_a, _b) : 0;
          });
          const parsedRole: Role[] = sortedResponse.map((role) => ({
            ...role,
            ...parsePermissions(role.permissions),
          }));
          setState(parsedRole as T);
          break;
        }
        // AlgorithmVersionsProvider
        case "algorithm_versions": {
          const response = await getAlgorithmVersions({ organizationId });
          setState(
            [...response].sort((a, b) => {
              const _a = parseDateTimeOrISODateTime(a.created_at);
              const _b = parseDateTimeOrISODateTime(b.created_at);
              return _a && _b ? compareDesc(_a, _b) : 0;
            }) as T
          );
          break;
        }
        // ViewTypesProvider
        case "view_masters": {
          const response = await getViewMasters({
            organizationId,
          });
          setState(response as T);
          break;
        }
        // ConnectMastersProvider
        case "connect_masters": {
          const response = await getConnectMasters({ organizationId });
          setState(
            [...response].sort((a, b) => {
              const _a = parseDateTimeOrISODateTime(a.created_at);
              const _b = parseDateTimeOrISODateTime(b.created_at);
              return _a && _b ? compareDesc(_a, _b) : 0;
            }) as T
          );
          break;
        }
        // ConnectsProvider
        case "connects": {
          const response = await getConnects({ organizationId });
          setState(
            [...response].sort((a, b) => {
              const _a = parseDateTimeOrISODateTime(a.created_at);
              const _b = parseDateTimeOrISODateTime(b.created_at);
              return _a && _b ? compareDesc(_a, _b) : 0;
            }) as T
          );
          break;
        }
        // ConnectAddonsProvider
        case "connect_masters_owned": {
          if (
            !isAADeveloperOrAbove &&
            !hasPermission("connect_masters_owned", "read")
          ) {
            setState(null);
            return;
          }
          const response = isAADeveloperOrAbove
            ? await getAdminAppConnectMasters({ organizationId })
            : await getAppConnectMasters({ organizationId });
          setState(
            [...response].sort((a, b) => {
              const _a = parseDateTimeOrISODateTime(a.created_at);
              const _b = parseDateTimeOrISODateTime(b.created_at);
              return _a && _b ? compareDesc(_a, _b) : 0;
            }) as T
          );
          break;
        }
        // FrontendAddonProvider
        case "frontend_addons_owned": {
          if (
            !isAADeveloperOrAbove &&
            !hasPermission("frontend_addons_owned", "read")
          ) {
            setState(null);
            return;
          }
          const response = isAADeveloperOrAbove
            ? await getAdminFrontendAddons({ organizationId })
            : await getFrontendAddons({ organizationId });

          setState(
            [...response].sort((a, b) => {
              const _a = parseDateTimeOrISODateTime(a.created_at);
              const _b = parseDateTimeOrISODateTime(b.created_at);
              return _a && _b ? compareDesc(_a, _b) : 0;
            }) as T
          );
          break;
        }
        // 上記以外のPermissonsKeyの場合は、何もしない
        // 特にversion_categoriesは、VersionCategoriesProviderで管理する
        default: {
          setState(null);
          break;
        }
      }
    } catch (err) {
      setError(() => {
        if (typeof err == "object") {
          const e = {
            ...err,
            message: getAPIErrorDetailAndMessage(err),
          } as Error;
          throw e;
        } else {
          throw new Error("不明なエラー");
        }
      });
    } finally {
      setIsLoadedState(true);
    }
  }, [me, organizationId, hasPermission, isAADeveloperOrAbove, permissionKey]);

  useEffect(() => {
    runReloadState();
  }, [runReloadState]);

  const stateCtxValue = useMemo(
    () => ({
      state,
      runReloadState,
      isLoadedState: isLoadedState && isLoadedMe,
    }),
    [state, runReloadState, isLoadedState]
  );

  return stateCtxValue;
};

// app_permission_mastersはPermissionsKey駆動のuseAPIに未統合のため例外的に専用フックで取得する。
export const useAppPermissionMastersAPI = () => {
  const { organizationId } = useAppRouteParams();
  const [state, setState] = useState<AppPermissionMasterRaw[]>([]);
  const [isLoadedState, setIsLoadedState] = useState<boolean>(false);
  const [_, setError] = useState<unknown>(null);

  const runReloadState = useCallback(async () => {
    try {
      if (!organizationId) {
        setState([]);
        return;
      }
      setIsLoadedState(false);
      const response = await getAppPermissionMasters({ organizationId });
      setState(response);
    } catch (err) {
      setState([]);
      setError(() => {
        if (typeof err == "object") {
          const e = {
            ...err,
            message: getAPIErrorDetailAndMessage(err),
          } as Error;
          throw e;
        } else {
          throw new Error("不明なエラー");
        }
      });
    } finally {
      setIsLoadedState(true);
    }
  }, [organizationId]);

  useEffect(() => {
    runReloadState();
  }, [runReloadState]);

  return useMemo(
    () => ({
      state,
      runReloadState,
      isLoadedState,
    }),
    [state, runReloadState, isLoadedState]
  );
};

// Endpoint Addon は管理系 (/admin/endpoint_addons) のみ提供されており、ロールベースの
// パーミッションでは制御されないため、PermissionsKey 駆動の useAPI には統合せず、
// aa:developer 以上のみがアクセスできる専用フックとして取得する。
export const useEndpointAddonsAPI = () => {
  const { organizationId } = useAppRouteParams();
  const { isAADeveloperOrAbove } = useAppAccount();
  const [state, setState] = useState<EndpointAddonRaw[] | null>(null);
  const [isLoadedState, setIsLoadedState] = useState<boolean>(false);
  const [_, setError] = useState<unknown>(null);

  const runReloadState = useCallback(async () => {
    try {
      if (!organizationId || !isAADeveloperOrAbove) {
        setState(null);
        return;
      }
      setIsLoadedState(false);
      const response = await getAdminEndpointAddons({ organizationId });
      setState(
        [...response].sort((a, b) => {
          const _a = parseDateTimeOrISODateTime(a.created_at);
          const _b = parseDateTimeOrISODateTime(b.created_at);
          return _a && _b ? compareDesc(_a, _b) : 0;
        })
      );
    } catch (err) {
      setState(null);
      setError(() => {
        if (typeof err == "object") {
          const e = {
            ...err,
            message: getAPIErrorDetailAndMessage(err),
          } as Error;
          throw e;
        } else {
          throw new Error("不明なエラー");
        }
      });
    } finally {
      setIsLoadedState(true);
    }
  }, [organizationId, isAADeveloperOrAbove]);

  useEffect(() => {
    runReloadState();
  }, [runReloadState]);

  return useMemo(
    () => ({
      state,
      runReloadState,
      isLoadedState,
    }),
    [state, runReloadState, isLoadedState]
  );
};

// Endpoint Addon マスタも組織内では org-scoped の管理者 API (getAdminEndpointAddonMasters)
// のみで扱われ、ロールベースのパーミッションでは制御されないため、PermissionsKey 駆動の
// useAPI には統合せず、aa:developer 以上のみがアクセスできる専用フックとして取得する。
export const useEndpointAddonMastersAPI = () => {
  const { organizationId } = useAppRouteParams();
  const { isAADeveloperOrAbove } = useAppAccount();
  const [state, setState] = useState<AppEndpointAddonMasterRaw[] | null>(null);
  const [isLoadedState, setIsLoadedState] = useState<boolean>(false);
  const [_, setError] = useState<unknown>(null);

  const runReloadState = useCallback(async () => {
    try {
      if (!organizationId || !isAADeveloperOrAbove) {
        setState(null);
        return;
      }
      setIsLoadedState(false);
      const { items } = await getAdminEndpointAddonMasters({ organizationId });
      setState(
        [...items].sort((a, b) => {
          const _a = parseDateTimeOrISODateTime(a.created_at);
          const _b = parseDateTimeOrISODateTime(b.created_at);
          return _a && _b ? compareDesc(_a, _b) : 0;
        })
      );
    } catch (err) {
      setState(null);
      setError(() => {
        if (typeof err == "object") {
          const e = {
            ...err,
            message: getAPIErrorDetailAndMessage(err),
          } as Error;
          throw e;
        } else {
          throw new Error("不明なエラー");
        }
      });
    } finally {
      setIsLoadedState(true);
    }
  }, [organizationId, isAADeveloperOrAbove]);

  useEffect(() => {
    runReloadState();
  }, [runReloadState]);

  return useMemo(
    () => ({
      state,
      runReloadState,
      isLoadedState,
    }),
    [state, runReloadState, isLoadedState]
  );
};
